import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { add, cloneDeep, cond, debounce, isArray, set } from 'lodash';
import { eachDeep } from "deepdash-es/standalone";
import { workflowStatusOptions } from '../../../../constants/constantList';
import { use } from 'react';
import { isUniqueItemName, validateWorkflowItemName } from '../../../../utils/utilFunctions';
import ErrorModal from '../../Forms/FieldDataValidation/ErrorModal';

const BasicErrorCondition = () => {
  // Setup and context
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree.body.selectedNode;
  const { translatedData } = rightTree.tree.body;
  const defaultDoNotLogChecked = selectedNode?.LOG_WFITEM_DETAILS === "FALSE" ? true : false;
  const [doNotLogChecked, setDoNotLogChecked] = useState(defaultDoNotLogChecked);
  const [errorHandler, setErrorHandler] = useState("");
  const defaultDisableErrorHandler = selectedNode?.DISABLE_ERROR_HANDLER === "TRUE" ? true : false;
  const [disableErrorHandler, setDisableErrorHandler] = useState(defaultDisableErrorHandler);
  const defaultArchiveData = selectedNode?.ARCHIVE_TRANSACTION_FILES === "TRUE" ? true : false;
  const [archiveData, setArchiveData] = useState(defaultArchiveData);
  const [commErrors, setCommErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState(false);
  const [translationErrors, setTranslationErrors] = useState(false);
  const [processingErrors, setProcessingErrors] = useState(false);
  const defaultOverrideStatus = selectedNode?.OVERRIDE_WORKFLOW_STATUS === "TRUE" ? true : false;
  const [overrideStatus, setOverrideStatus] = useState(defaultOverrideStatus);
  const defaultWorkflowStatus = selectedNode?.WORKFLOW_STATUS_FOR_OVERRIDE || "";
  const [workflowStatus, setWorkflowStatus] = useState(defaultWorkflowStatus);
  const defaultStopWorkflow = selectedNode?.STOP_WORKFLOW === "TRUE" ? true : false;
  const [stopWorkflow, setStopWorkflow] = useState(defaultStopWorkflow);
  let defaultErrorHandling = selectedNode?.RETAIN_ALL_ERRORS === "TRUE" ? "RETAIN_ALL_ERRORS_TRUE" : selectedNode?.RETAIN_UNREPORTED_ERRORS === "TRUE" ? "RETAIN_UNREPORTED_ERRORS" : "RETAIN_ALL_ERRORS_FALSE";
  const [errorHandling, setErrorHandling] = useState(defaultErrorHandling);
  const errorHandlers = useSelector(state => state?.treeData?.[parentScreen]?.rightTree?.tree?.templateHeader?.WORKFLOW?.ERROR_HANDLER_LIST || []);
  const [errorHandlerOptions, setErrorHandlerOptions] = useState([]);
  const [fieldsDisabled, setFieldsDisabled] = useState(true);
  const [nameError, setNameError] = useState("");
  
  // Name validation states
  const [nameErrorModal, setNameErrorModal] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  // Memoized value that only changes when OUTBOUND_NAME or ID values change
  const errorHandlerNames = useMemo(() => {
    return errorHandlers.map(handler => ({
      name: handler.OUTBOUND_NAME,
      id: handler.ID
    }));
  }, [errorHandlers]);

  const workdlowStatusMenuOptions = workflowStatusOptions.map((item) => (
    <MenuItem key={item.value} value={item.value}>
      {t(item.text)}
    </MenuItem>
  ));

  const submitChange = (key, value, bulk) => {
    if (!key && !bulk) return;

    // Clone and deeply iterate through the tree data to update the relevant node
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;

      if (bulk) {
        bulk.forEach((item) => {
          if (item.value === null) {
            if (obj?.hasOwnProperty(item.key)) delete obj[item.key];
          } else {
            obj[item.key] = item.value;
          }
        });
      } else {
        if (key === "NAME") {
          obj[key] = value;
          obj.title = value; // Update the title for the node
        } else {
          if (value === null) {
            if (obj?.hasOwnProperty(key)) delete obj[key];
          } else {
            obj[key] = value;
          }
        }
      }

      // Dispatch an action to update the selected node in the state
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      );

      // If the object has children, return them for further iteration
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    // Dispatch an action to update the translated tree data in the state
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);

  // Name field state
  const defaultName = selectedNode?.NAME || "";
  const [name, setName] = useState(defaultName);
  const wfItemId = selectedNode?.ID;

  // Handle changes to the name field
  const handleNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
      // handleNameBlur(e.target.value);
    }
  }

  // Handle blur event on name field
  const handleNameBlur = (eVal) => {
    const oldName = selectedNode?.NAME || '';
    
    const validationResult = validateWorkflowItemName(
      eVal, 
      oldName, 
      translatedData, 
      selectedNode?.key, 
      'ERROR_HANDLER_CONDITION'
    );
    
    if (validationResult.isValid) {
      const newName = eVal?.trim() || '';
      setName(newName);
      if (newName !== oldName) {
        // Mark template as changed immediately to ensure flag is set before tab switch
        dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
        submitChange('NAME', newName);
        // Wait for debounce delay + buffer before dispatching
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 200);
      } else {
        // If no change, dispatch immediately
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 100);
      }
    } else {
      if (validationResult.errorMessage) {
        setNameErrorMessage(t(validationResult.errorMessage));
        setNameErrorModal(true);
      } else if (validationResult.shouldRevert) {
        setName(oldName);
      }
      // For error cases, dispatch immediately
      setTimeout(() => {
        dispatch(treeActions.setIsInputFocusedFalse());
      }, 100);
    }
  }

  const handleNameErrorModal = (isOpen) => {
    setNameErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setName(oldName);
    }
  };


  const handleDoNotLogChange = (e) => {
    const checked = e?.target?.checked;
    setDoNotLogChecked(checked);
    debounceChangeHandler('LOG_WFITEM_DETAILS', checked ? 'FALSE' : 'TRUE');
  }


  const handleErrorHandlerChange = (e) => {
    const value = e.target.value;
    setErrorHandler(e.target.value);
    if (value === t("SELECT")) setFieldsDisabled(true);
    else setFieldsDisabled(false);
    
    // Find the selected error handler to get the ID
    const selectedErrorHandler = errorHandlers.find(handler => handler.OUTBOUND_NAME === value);
    const dataId = selectedErrorHandler ? selectedErrorHandler.ID : null;

    debounceChangeHandler(null, null, [
      { key: "OUTBOUND_NAME", value: value === t("SELECT") ? null : e.target.value },
      { key: "OUTBOUND_ID", value: value === t("SELECT") ? null : dataId },
    ]);
  }


  const handleDisableErrorHandlerChange = (e) => {
    const checked = e?.target?.checked;
    setDisableErrorHandler(checked);
    debounceChangeHandler('DISABLE_ERROR_HANDLER', checked ? 'TRUE' : 'FALSE');
  }


  const handleArchiveDataChange = (e) => {
    const checked = e?.target?.checked;
    setArchiveData(checked);
    debounceChangeHandler('ARCHIVE_TRANSACTION_FILES', checked ? 'TRUE' : 'FALSE');
  }

  const addErrorHandlerCondition = (checked, condition) => {
    const clonedData = JSON.parse(JSON.stringify(translatedData, null, 2));
    // Clone and deeply iterate through the tree data to update the relevant node
    const updatedData = eachDeep(clonedData, (obj) => {
      if (obj?.key !== selectedNode?.key) return;
      let found = false;
      if (isArray(obj?.CONDITION)) {
        obj.CONDITION = obj?.CONDITION?.map((item) => {
          if (item?.BASIC_GROUP === "TRUE") {
            found = true;
            if (checked) {
              item.CONDITION.push(
                {
                  "NAME": t(condition),
                  "COMPARE": "TRUE",
                  "RECORD_TYPE": "SYSTEM_DATA",
                  "MAPNAME": condition,
                  "OPERATOR": item?.CHILDOPERATOR,
                  "WF_ITEM_DEPENDENCY_TIMEOUT": "5000",
                  "COMPARE_ACTION_TRIM": "FALSE",
                  "COMPARE_ACTION_IGNORECASE": "FALSE",
                  "COMPARE_ACTION_NOT": "FALSE",
                }
              )
            } else {
              item.CONDITION = item?.CONDITION?.filter((conditionItem) => {
                return conditionItem?.MAPNAME !== condition;
              })
              
            }
          }
          return item;
        });
      }
      if (!found) {
        obj.CONDITION = obj?.CONDITION || [];
        obj.CONDITION.push(
          {
            "NAME": "Basic Error Conditions",
            "CHILDOPERATOR": "OR",
            "WF_ITEM_DEPENDENCY_TIMEOUT": "5000",
            "COMPARE_ACTION_TRIM": "FALSE",
            "COMPARE_ACTION_IGNORECASE": "FALSE",
            "COMPARE_ACTION_NOT": "FALSE",
            "BIT_MAP": "",
            "BASIC_GROUP": "TRUE",
            "CONDITION": [
              {
                "NAME": t(condition),
                "COMPARE": "TRUE",
                "RECORD_TYPE": "SYSTEM_DATA",
                "MAPNAME": condition,
                "OPERATOR": obj?.CHILDOPERATOR,
                "WF_ITEM_DEPENDENCY_TIMEOUT": "5000",
                "COMPARE_ACTION_TRIM": "FALSE",
                "COMPARE_ACTION_IGNORECASE": "FALSE",
                "COMPARE_ACTION_NOT": "FALSE",
              }
            ]
          }
        )
      }
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      );
    });
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  }


  const handleCommErrorsChange = (e) => {
    const checked = e?.target?.checked;
    setCommErrors(checked);
    addErrorHandlerCondition(checked, 'COMMUNICATION_ERROR');
  }


  const handleValidationErrorsChange = (e) => {
    const checked = e?.target?.checked;
    setValidationErrors(checked);
    addErrorHandlerCondition(checked, 'VALIDATION_ERRORS');
  }


  const handleTranslationErrorsChange = (e) => {
    const checked = e?.target?.checked;
    setTranslationErrors(checked);
    addErrorHandlerCondition(checked, 'TRANSLATION_ERRORS');
  }


  const handleProcessingErrorsChange = (e) => {
    const checked = e?.target?.checked;
    setProcessingErrors(checked);
    addErrorHandlerCondition(checked, 'PROCESSING_ERRORS');
  }


  const handleOverrideStatusChange = (e) => {
    const checked = e?.target?.checked;
    setOverrideStatus(checked);
    debounceChangeHandler('OVERRIDE_WORKFLOW_STATUS', checked ? 'TRUE' : 'FALSE');
  }


  const handleWorkflowStatusChange = (e) => {
    setWorkflowStatus(e.target.value);
    debounceChangeHandler('WORKFLOW_STATUS_FOR_OVERRIDE', e.target.value);
  }


  const handleErrorHandlingChange = (e) => {
    setErrorHandling(e.target.value);
    if (e.target.value === 'RETAIN_ALL_ERRORS_TRUE') {
      debounceChangeHandler(null, null, [
        { key: "RETAIN_ALL_ERRORS", value: "TRUE" },
        { key: "RETAIN_UNREPORTED_ERRORS", value: "FALSE" },
      ]);
    } else if (e.target.value === 'RETAIN_UNREPORTED_ERRORS') {
      debounceChangeHandler(null, null, [
        { key: "RETAIN_ALL_ERRORS", value: "FALSE" },
        { key: "RETAIN_UNREPORTED_ERRORS", value: "TRUE" },
      ]);
    } else if (e.target.value === 'RETAIN_ALL_ERRORS_FALSE') {
      debounceChangeHandler(null, null, [
        { key: "RETAIN_ALL_ERRORS", value: "FALSE" },
        { key: "RETAIN_UNREPORTED_ERRORS", value: "FALSE" },
      ]);
    }
  }

  const handleStopWorkflowChange = (e) => {
    const checked = e?.target?.checked;
    setStopWorkflow(checked);
    debounceChangeHandler('STOP_WORKFLOW', checked ? 'TRUE' : 'FALSE');
  }

  useEffect(() => {
    
    const errorHandlerOptions = [{name: t("SELECT")}, ...(errorHandlerNames || [])].map((item) => (
      <MenuItem key={item} data-Id={item?.id} value={item?.name}>
        {item?.name}
      </MenuItem>
    ));
    setErrorHandlerOptions(errorHandlerOptions);
    setFieldsDisabled(!selectedNode?.OUTBOUND_NAME || selectedNode?.OUTBOUND_NAME === t("SELECT") || errorHandlerOptions?.filter(e => e.props["data-Id"] === selectedNode?.OUTBOUND_ID)?.length === 0);
    const foundErrorHandler = errorHandlerOptions?.filter(e => e.props["data-Id"] == selectedNode?.OUTBOUND_ID)?.length ? errorHandlerOptions?.filter(e => e.props["data-Id"] == selectedNode?.OUTBOUND_ID)?.[0]?.props?.value : null;
    if (foundErrorHandler) {
      setErrorHandler(foundErrorHandler);
      if(selectedNode?.OUTBOUND_NAME !== foundErrorHandler) {
        debounceChangeHandler(null, null, [
          { key: "OUTBOUND_NAME", value: foundErrorHandler },
          { key: "OUTBOUND_ID", value: selectedNode?.OUTBOUND_ID },
        ]);
      }
    } else {
      const filterErrorHandler = errorHandlerOptions?.filter(e => e.props["data-Id"] == selectedNode?.OUTBOUND_ID);
      debounceChangeHandler(null, null, [
        { key: "OUTBOUND_NAME", value: null },
        { key: "OUTBOUND_ID", value: null },
      ]);
      setErrorHandler(filterErrorHandler?.[0]?.props?.value || t("SELECT"));
    }
  }, [selectedNode?.OUTBOUND_NAME, selectedNode?.OUTBOUND_ID, errorHandlerNames]);


  useEffect(() => {
    let errorsType = {
      communicationError: false,
      validationError: false,
      translationError: false,
      processingError: false,
    }
    if (selectedNode?.CONDITION?.length > 0) {
      selectedNode?.CONDITION?.forEach((item) => {
        if (item?.BASIC_GROUP === "TRUE") {
          item?.CONDITION?.forEach((condition) => {
            if (condition?.MAPNAME === "COMMUNICATION_ERROR") {
              errorsType.communicationError = true;
            } else if (condition?.MAPNAME === "VALIDATION_ERRORS") {
              errorsType.validationError = true;
            } else if (condition?.MAPNAME === "TRANSLATION_ERRORS") {
              errorsType.translationError = true;
            } else if (condition?.MAPNAME === "PROCESSING_ERRORS") {
              errorsType.processingError = true;
            }
          })
        }
      });
    }
    if (errorsType?.communicationError) setCommErrors(true);
    else setCommErrors(false);
    if (errorsType?.validationError) setValidationErrors(true);
    else setValidationErrors(false);
    if (errorsType?.translationError) setTranslationErrors(true);
    else setTranslationErrors(false);
    if (errorsType?.processingError) setProcessingErrors(true);
    else setProcessingErrors(false);

    // Check if any error condition is selected to enable/disable fields
  }, [selectedNode]);

  useEffect(() => {
    // Set initial state based on selected node properties
    setName(selectedNode?.NAME || "");
  }, [selectedNode?.NAME],)




  return (
    <Box >
      {/* Name field */}
      <FormControl variant='outlined' className='width-100' size='small' sx={{ marginBottom: 2 }}>
        <InputLabel
          htmlFor='error-handler-name-input'
          sx={{ padding: "0 6px !important", left: "-5px" }}
        >
          {t('COL_NAME')}
        </InputLabel>
        <OutlinedInput
          id="error-handler-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          endAdornment={<div style={styles.nameAdornment}>{`ID: ${wfItemId}`}</div>}
          sx={{ justifyContent: "flex-end" }}
          onChange={(e) => handleNameChange(e)}
          onBlur={(e) => handleNameBlur(e?.target?.value)}
          onFocus={() => dispatch(treeActions.setIsInputFocusedTrue())}
        />
      </FormControl>

      {/* Do not log workflow item details checkbox */}
      <FormControl variant='outlined' className='width-100' size='small' sx={{ marginBottom: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={doNotLogChecked}
              onChange={handleDoNotLogChange}
              name='doNotLogChecked'
              color='primary'
            />
          }
          label={t('LBL_LOG_NO_WFITEM_DETAILS')}
        />
      </FormControl>

      {/* Error handler dropdown */}
      <FormControl variant='outlined' className='width-100' size='small' sx={{ marginBottom: 2 }}>
        <InputLabel>{t('TIT_ERROR_HANDLDER')}</InputLabel>
        <Select
          value={errorHandlerOptions?.filter(e => e.props.value === errorHandler)?.length ? errorHandler : t("SELECT")}
          onChange={handleErrorHandlerChange}
          label={t('TIT_ERROR_HANDLDER')}
        >
          {errorHandlerOptions}
        </Select>
      </FormControl>

      {/* Disable error handler and archive data checkboxes in row */}
      <FormGroup
        sx={{
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap'
        }}
      >
        <FormControlLabel
          sx={{ minWidth: '180px', marginRight: 2 }}
          control={
            <Checkbox
              checked={disableErrorHandler}
              onChange={handleDisableErrorHandlerChange}
              name='disableErrorHandler'
              color='primary'
              disabled={fieldsDisabled}
            />
          }
          label={t('LBL_DISABLE_ERROR_HANDLER')}
        />
        <FormControlLabel
          sx={{ minWidth: '180px' }}
          control={
            <Checkbox
              checked={archiveData}
              onChange={handleArchiveDataChange}
              name='archiveData'
              color='primary'
              disabled={fieldsDisabled}
            />
          }
          label={t('LBL_ARCHIVE_DATA')}
        />
      </FormGroup>

      {/* Error types section */}
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="body1" gutterBottom>
          {t('LBL_BASIC_ERROR_HANDLER_SELECT_ERROR_TYPES')}
        </Typography>
        <FormGroup
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap'
          }}
        >
          <FormControlLabel
            sx={{ minWidth: '180px', marginRight: 2 }}
            control={
              <Checkbox
                disabled={fieldsDisabled}
                checked={commErrors}
                onChange={handleCommErrorsChange}
                name='commErrors'
                color='primary'
              />
            }
            label={t('LBL_COMMUNICATION_ERRORS')}
          />
          <FormControlLabel
            sx={{ minWidth: '180px', marginRight: 2 }}
            control={
              <Checkbox
                disabled={fieldsDisabled}
                checked={validationErrors}
                onChange={handleValidationErrorsChange}
                name='validationErrors'
                color='primary'
              />
            }
            label={t('LBL_VALIDATION_ERRORS')}
          />
          <FormControlLabel
            sx={{ minWidth: '180px', marginRight: 2 }}
            control={
              <Checkbox
                disabled={fieldsDisabled}
                checked={translationErrors}
                onChange={handleTranslationErrorsChange}
                name='translationErrors'
                color='primary'
              />
            }
            label={t('LBL_TRANSLATION_ERRORS')}
          />
          <FormControlLabel
            sx={{ minWidth: '180px' }}
            control={
              <Checkbox
                disabled={fieldsDisabled}
                checked={processingErrors}
                onChange={handleProcessingErrorsChange}
                name='processingErrors'
                color='primary'
              />
            }
            label={t('LBL_PROCESSING_ERRORS')}
          />
        </FormGroup>
      </Box>

      {/* Error handler processing options */}
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="body1" gutterBottom>
          {t('LBL_OVERRIDE_WORKFLOW_STATUS')}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={overrideStatus}
                onChange={handleOverrideStatusChange}
                name='overrideStatus'
                color='primary'
              />
            }
            label={t('LBL_OVERRIDE_WORKFLOW_STATUS')}
          />
        </FormGroup>
        {overrideStatus && (
          <Box sx={{ marginTop: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('LBL_WORKFLOW_STATUS_FOR_OVERRIDE')}</InputLabel>
              <Select
                value={workflowStatus}
                onChange={handleWorkflowStatusChange}
                label={t('LBL_WORKFLOW_STATUS_FOR_OVERRIDE')}
                
              >
                {workdlowStatusMenuOptions}
                {/* Add more options as needed */}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Stop workflow processing section */}
      <Box>
        <FormControlLabel
          sx={{ minWidth: '180px', marginRight: 2 }}
          control={
            <Checkbox
              checked={stopWorkflow}
              onChange={handleStopWorkflowChange}
              name='stopWorkflow'
              color='primary'
              disabled={fieldsDisabled}
            />
          }
          label={t('LBL_STOP_PROCESSING_IF_ERROR_OCCURS')}
        />
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={errorHandling}
            name="errorHandling"
            onChange={handleErrorHandlingChange}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap'
            }}
          >
            <FormControlLabel
              sx={{ minWidth: '180px', marginRight: 2 }}
              value="RETAIN_ALL_ERRORS_TRUE"
              control={<Radio disabled={fieldsDisabled} />}
              label={t('BTN_RETAIN_ALL_ERRORS')}
            />
            <FormControlLabel
              sx={{ minWidth: '220px', marginRight: 2 }}
              value="RETAIN_UNREPORTED_ERRORS"
              control={<Radio disabled={fieldsDisabled} />}
              label={t('BTN_RETAIN_UNREPORTED_ERRORS')}
            />
            <FormControlLabel
              sx={{ minWidth: '180px' }}
              value="RETAIN_ALL_ERRORS_FALSE"
              control={<Radio disabled={fieldsDisabled} />}
              label={t('BTN_CLEAR_ALL_ERRORS')}
            />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Error Modal for name validation */}
      <ErrorModal
        errorModal={nameErrorModal}
        setErrorModal={setNameErrorModal}
        errorMessage={nameErrorMessage}
        errorType={t('ERROR')}
        handleErrorModal={handleNameErrorModal}
      />
    </Box>
  );
};

const styles = {
  nameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

export default BasicErrorCondition;
